import React from "react";
import { http } from "@/lib/api";
import { Image as ImageIcon, Clapperboard } from "lucide-react";

function toEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  return null;
}

export default function Galerie() {
  const [items, setItems] = React.useState([]);
  const [kind, setKind] = React.useState("all");
  React.useEffect(() => { http.get("/gallery").then(r => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([])); }, []);

  const visible = items.filter(i => i.pole !== "Afrolat'Studio" && (kind === "all" || i.kind === kind));
  const photos = visible.filter(i => i.kind !== "video");
  const videos = visible.filter(i => i.kind === "video");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
      <p className="he-overline text-neutral-500">Heel’Events</p>
      <h1 className="font-heading text-5xl sm:text-6xl font-black tracking-tighter mt-3">Galerie</h1>
      <p className="mt-4 text-neutral-600 font-body max-w-2xl">
        Photos et vidéos de nos cours, stages, spectacles et événements.
      </p>

      <div className="mt-8 flex flex-wrap gap-2">
        {[["all", "Tout voir"], ["photo", "Photos"], ["video", "Vidéos"]].map(([v, l]) => (
          <button
            key={v}
            data-testid={`galerie-filter-${v}`}
            onClick={() => setKind(v)}
            className={`rounded-full px-4 py-2 text-xs border transition-colors ${
              kind === v ? "bg-[var(--he-red)] text-white border-[var(--he-red)]" : "border-black/15 hover:border-[var(--he-red)]"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="mt-14 p-10 border border-dashed border-black/15 rounded-2xl text-center text-neutral-500">
          La galerie arrive bientôt — photos et vidéos en cours d’ajout. En attendant, retrouvez-nous sur Instagram
          <a href="https://instagram.com/heelevents_afrolatstudio" target="_blank" rel="noreferrer" className="text-[var(--he-red)] font-bold"> @heelevents_afrolatstudio</a>.
        </div>
      ) : (
        <>
          {photos.length > 0 && (
            <section className="mt-12">
              <h2 className="font-heading text-2xl font-bold flex items-center gap-2 mb-6"><ImageIcon className="w-5 h-5 text-[var(--he-red)]" /> Photos</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {photos.map((p) => (
                  <figure key={p.id} data-testid="galerie-photo" className="group relative overflow-hidden rounded-xl aspect-square bg-neutral-100">
                    <img src={p.url} alt={p.title} loading="lazy" className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
                    {p.title && (
                      <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent text-white text-xs p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        {p.title}{p.pole ? ` · ${p.pole}` : ""}
                      </figcaption>
                    )}
                  </figure>
                ))}
              </div>
            </section>
          )}
          {videos.length > 0 && (
            <section className="mt-14">
              <h2 className="font-heading text-2xl font-bold flex items-center gap-2 mb-6"><Clapperboard className="w-5 h-5 text-[var(--he-red)]" /> Vidéos</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {videos.map((v) => {
                  const embed = toEmbed(v.url);
                  return (
                    <figure key={v.id} data-testid="galerie-video" className="rounded-xl overflow-hidden border border-black/10">
                      <div className="aspect-video bg-black">
                        {embed ? (
                          <iframe src={embed} title={v.title} className="w-full h-full" allowFullScreen />
                        ) : (
                          <video src={v.url} controls className="w-full h-full" />
                        )}
                      </div>
                      {v.title && <figcaption className="p-3 text-sm font-body">{v.title}{v.pole ? ` · ${v.pole}` : ""}</figcaption>}
                    </figure>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
